import * as DialogPrimitive from "@radix-ui/react-dialog";
import { X } from "lucide-react";
import { useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import {
  GetAllCompanyDto,
  GetCustomV1BoResponse,
  GetOneExpeditionBoResponseDto,
} from "@/types/api/api-types";
import { ExpeditionBoDeliveryInfoFormData } from "@/types/forms/bo/expeditionBo.form";
import { ImporterSelect } from "../../importerSelect";
import { SideContentAdditionalInfo } from "./sideContentAdditionalInfo";
import { SideContentDocuments } from "./sideContentDocuments";
import { SideContentOptions } from "./sideContentOptions";
import { SideContentProductInfo } from "./sideContentProductInfo";

type SideContentProps = {
  expedition: GetOneExpeditionBoResponseDto;
  company: GetAllCompanyDto;
  custom?: GetCustomV1BoResponse;
  readOnly?: boolean;
};

const SideContentBody = ({ expedition, company, custom, readOnly = false }: SideContentProps) => {
  const { t } = useTranslation("boExpeditionForm");
  const methods = useFormContext<ExpeditionBoDeliveryInfoFormData>();
  const formCountryCode =
    useWatch({ control: methods.control, name: "deliveryInfo.countryCode" }) ?? "";
  const countryCode = readOnly ? expedition.deliveryInfo?.country : formCountryCode;
  const isUsaExpedition = countryCode === "US";

  return (
    <div className="shadow-basic flex w-full flex-col items-start justify-center gap-4 rounded-xl border border-gray-200 bg-white p-6">
      <div className="flex w-full flex-col gap-2 rounded-md border border-blue-200 bg-blue-50 p-3">
        <p className="text-sm font-medium text-blue-700">{t("sidePanel.customerReference")}</p>
        <p className="text-sm font-semibold text-gray-900">
          {expedition.expeditionBillingDetail.reference || "-"}
        </p>
      </div>
      <div className="flex w-full flex-col gap-2 rounded-md border border-yellow-200 bg-yellow-50 p-3">
        <p className="text-sm font-medium text-yellow-800">{t("sidePanel.customerComment")}</p>
        <p className="text-sm font-semibold whitespace-pre-line text-gray-900">
          {expedition.deliveryInfo?.comment || "-"}
        </p>
      </div>
      <SideContentOptions custom={custom} expedition={expedition} readOnly={readOnly} />
      {isUsaExpedition && (
        <ImporterSelect originalImporterId={expedition.importerId} readOnly={readOnly} />
      )}
      <SideContentProductInfo expedition={expedition} />
      <SideContentDocuments expedition={expedition} />
      <SideContentAdditionalInfo expedition={expedition} company={company} readOnly={readOnly} />
    </div>
  );
};

export const SideContent = (props: SideContentProps) => {
  return <SideContentBody {...props} />;
};

export const SideContentMobileModal = ({
  open,
  onClose,
  expedition,
  company,
  custom,
  readOnly,
}: {
  open: boolean;
  onClose: () => void;
  expedition: GetOneExpeditionBoResponseDto;
  company: GetAllCompanyDto;
  custom?: GetCustomV1BoResponse;
  readOnly?: boolean;
}) => {
  const { t } = useTranslation(["boExpeditionForm", "common"]);
  return (
    <DialogPrimitive.Root open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay
          className={cn(
            "fixed inset-0 z-50 bg-black/50",
            "data-[state=open]:animate-in data-[state=closed]:animate-out",
            "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
          )}
        />
        <DialogPrimitive.Content
          className={cn(
            "bg-background fixed inset-y-0 right-0 z-50 flex h-full w-full flex-col overflow-y-auto border-l shadow-xl duration-300",
            "data-[state=open]:animate-in data-[state=closed]:animate-out",
            "data-[state=open]:slide-in-from-right data-[state=closed]:slide-out-to-right",
          )}
        >
          <DialogPrimitive.Title className="sr-only">
            {t("sidePanel.mobileModalTitle")}
          </DialogPrimitive.Title>
          <DialogPrimitive.Close
            className="absolute top-4 right-4 z-10 rounded-sm opacity-70 transition-opacity hover:opacity-100"
            aria-label={t("common:actions.close")}
          >
            <X className="h-5 w-5" />
          </DialogPrimitive.Close>
          <div className="flex flex-col gap-4 p-4 pt-12">
            <SideContentBody
              expedition={expedition}
              company={company}
              custom={custom}
              readOnly={readOnly}
            />
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
};
